import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { resolveFileUrl } from '../../utils/fileUrl';
import { User, CheckCircle, Clock, XCircle, Loader2, Lock, LogOut } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  approved: { label: 'VERIFIED', bg: 'bg-emerald-50', border: 'border-emerald-400', text: 'text-emerald-700', Icon: CheckCircle },
  pending: { label: 'PENDING APPROVAL', bg: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-700', Icon: Clock },
  rejected: { label: 'REJECTED', bg: 'bg-red-50', border: 'border-red-400', text: 'text-red-700', Icon: XCircle },
};

export default function PSVerifyIdPage() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkInResult, setCheckInResult] = useState(null);
  const [gateStaffSession, setGateStaffSession] = useState(() => {
    try {
      const raw = localStorage.getItem('psGateStaff');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.token || !parsed?.expiresAt) return null;
      if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
        localStorage.removeItem('psGateStaff');
        return null;
      }
      return parsed;
    } catch { return null; }
  });
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinLoading, setPinLoading] = useState(false);

  const handlePinUnlock = useCallback(async (e) => {
    e?.preventDefault?.();
    setPinError('');
    const pin = String(pinInput || '').trim();
    if (!/^\d{4,8}$/.test(pin)) {
      setPinError('Enter a 4–8 digit PIN');
      return;
    }
    setPinLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/ps/checkin/auth`,
        { pin },
        { headers: { 'Content-Type': 'application/json' }, timeout: 15000 }
      );
      const session = { token: res.data.token, expiresAt: res.data.expiresAt };
      localStorage.setItem('psGateStaff', JSON.stringify(session));
      setGateStaffSession(session);
      setPinInput('');
    } catch (err) {
      // axios populates err.response on HTTP errors (4xx/5xx); otherwise it's a network/timeout
      if (err?.response) {
        const detail = err.response.data?.detail || `Error ${err.response.status}`;
        setPinError(typeof detail === 'string' ? detail : 'Authentication failed');
      } else {
        // True network error — surface raw message so user can report it
        const msg = err?.message ? `Network error: ${err.message}` : 'Network error. Check your connection.';
        setPinError(msg);
        // eslint-disable-next-line no-console
        console.error('Gate staff PIN auth error:', err);
      }
    } finally {
      setPinLoading(false);
    }
  }, [pinInput]);

  const handleGateStaffLogout = useCallback(() => {
    localStorage.removeItem('psGateStaff');
    setGateStaffSession(null);
    setCheckInResult(null);
  }, []);

  useEffect(() => {
    if (!code) { setError('No verification code provided'); setLoading(false); return; }
    fetch(`${API}/api/ps/verify/${code}`)
      .then(r => r.json())
      .then(d => {
        if (!d.valid) { setError(d.message || 'Invalid code'); }
        else { setData(d); }
      })
      .catch(() => setError('Failed to verify. Please try again.'))
      .finally(() => setLoading(false));
  }, [code]);

  const handleCheckIn = useCallback(async () => {
    if (checkingIn) return;
    if (!gateStaffSession?.token) {
      setCheckInResult({ success: false, message: 'Gate Staff PIN required' });
      return;
    }
    setCheckingIn(true);
    try {
      const res = await axios.post(
        `${API}/api/ps/checkin/${code}`,
        {},
        { headers: { 'Authorization': `Bearer ${gateStaffSession.token}` }, timeout: 15000 }
      );
      setCheckInResult({ success: true, ...res.data });
      setData(prev => ({ ...prev, checkedIn: true, checkedInAt: res.data.checkedInAt }));
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem('psGateStaff');
        setGateStaffSession(null);
        setCheckInResult({ success: false, message: err.response.data?.detail || 'Session expired. Please re-enter the PIN.' });
        return;
      }
      if (err?.response) {
        setCheckInResult({ success: false, message: err.response.data?.detail || `Error ${err.response.status}` });
      } else {
        // eslint-disable-next-line no-console
        console.error('Check-in network error:', err);
        setCheckInResult({ success: false, message: err?.message ? `Network error: ${err.message}` : 'Network error. Please try again.' });
      }
    } finally {
      setCheckingIn(false);
    }
  }, [code, checkingIn, gateStaffSession]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="verify-loading">
        <Loader2 className="w-10 h-10 animate-spin text-orange-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" data-testid="verify-error">
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Verification Failed</h2>
          <p className="text-gray-500 text-sm">{error || 'Invalid verification code'}</p>
          <p className="text-gray-400 text-xs mt-4">Code: {code}</p>
        </div>
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[data.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusCfg.Icon;
  const isCandidate = data.type === 'Candidate';
  const headerGradient = isCandidate
    ? 'from-orange-600 to-amber-500'
    : 'from-blue-700 to-blue-500';

  const isCheckedIn = data.checkedIn;
  const canCheckIn = data.status === 'approved' && !isCheckedIn;

  const formatTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4" data-testid="verify-page">
      <div className="max-w-sm w-full bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className={`bg-gradient-to-r ${headerGradient} px-6 py-5 text-center text-white`}>
          <h1 className="text-lg font-bold tracking-wide" data-testid="verify-title">PARICHAY SANGAM 2026</h1>
          <p className="text-white/80 text-xs mt-1">e-ID Verification / ई-आईडी सत्यापन</p>
        </div>

        {/* Status Badge */}
        <div className={`mx-6 -mt-4 ${statusCfg.bg} border-2 ${statusCfg.border} rounded-xl px-4 py-3 flex items-center justify-center gap-2`} data-testid="verify-status">
          <StatusIcon className={`w-5 h-5 ${statusCfg.text}`} />
          <span className={`text-base font-bold ${statusCfg.text}`}>{statusCfg.label}</span>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Photo — always shown */}
          <div className="flex justify-center">
            {data.photoUrl ? (
              <img
                src={resolveFileUrl(data.photoUrl)}
                alt={data.name}
                className="w-24 h-28 object-cover rounded-xl border-2 border-gray-200 shadow-sm"
                data-testid="verify-photo"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
            ) : null}
            <div
              className={`w-24 h-28 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 items-center justify-center ${data.photoUrl ? 'hidden' : 'flex'}`}
              data-testid="verify-photo-placeholder"
            >
              <User className="w-10 h-10 text-gray-300" />
            </div>
          </div>

          {/* Name + Type */}
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900" data-testid="verify-name">{data.name}</h2>
            <span className={`inline-block mt-1 px-3 py-0.5 rounded-full text-xs font-semibold ${
              isCandidate ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
            }`} data-testid="verify-type">
              {data.type}
            </span>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            {data.category && <DetailItem label="Category" value={data.category} testId="verify-category" />}
            {data.position && <DetailItem label="Position" value={data.position} testId="verify-position" />}
            {data.nukh && <DetailItem label="Nukh" value={data.nukh} testId="verify-nukh" />}
            {data.currentPlace && <DetailItem label="Current Place" value={data.currentPlace} testId="verify-place" />}
            {data.relation && <DetailItem label="Relation" value={data.relation} testId="verify-relation" />}
            {data.gender && <DetailItem label="Gender" value={data.gender} testId="verify-gender" />}
            {data.fatherOrHusbandName && <DetailItem label="Father / Husband" value={data.fatherOrHusbandName} testId="verify-father" />}
            <DetailItem
              label={data.type === 'Member' ? 'Member No.' : 'Family Code'}
              value={data.familyCode}
              testId="verify-family-code"
            />
            {(data.candidateCode || data.attendeeCode || data.registrationCode) && (
              <DetailItem
                label={data.type === 'Member' ? 'Reg. Code' : 'Code'}
                value={data.candidateCode || data.attendeeCode || data.registrationCode}
                testId="verify-code"
              />
            )}
            {data.roomNumber && <DetailItem label="Room No." value={data.roomNumber} testId="verify-room" />}
            {data.seatNumber && <DetailItem label="Seat No." value={data.seatNumber} testId="verify-seat" />}
            {data.guardianName && <DetailItem label="Guardian" value={data.guardianName} testId="verify-guardian" />}
          </div>

          {/* Check-in Section */}
          {isCheckedIn ? (
            <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4 text-center" data-testid="checkin-done">
              <CheckCircle className="w-8 h-8 text-emerald-600 mx-auto mb-1" />
              <p className="text-emerald-700 font-bold text-sm">CHECKED IN</p>
              <p className="text-emerald-600 text-xs mt-1">{formatTime(data.checkedInAt)}</p>
            </div>
          ) : canCheckIn && !gateStaffSession ? (
            /* PIN unlock — only gate staff with the PIN can reveal the CHECK IN button */
            <form onSubmit={handlePinUnlock} className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2" data-testid="gate-staff-pin-form">
              <div className="flex items-center gap-2 text-slate-700">
                <Lock className="w-4 h-4" />
                <p className="text-sm font-semibold">Gate Staff PIN required</p>
              </div>
              <p className="text-[11px] text-slate-500 leading-snug">Only authorized gate staff can perform check-ins. Enter the event PIN to unlock this device for 12 hours.</p>
              <input
                type="password"
                inputMode="numeric"
                pattern="\d*"
                maxLength={8}
                autoComplete="off"
                placeholder="Enter PIN"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none text-center text-lg tracking-[0.4em] font-semibold"
                data-testid="gate-staff-pin-input"
              />
              {pinError && <p className="text-red-500 text-xs text-center" data-testid="gate-staff-pin-error">{pinError}</p>}
              <button
                type="submit"
                disabled={pinLoading}
                className="w-full py-2.5 rounded-lg font-bold text-white text-sm bg-slate-800 hover:bg-slate-900 disabled:bg-gray-400 transition-all"
                data-testid="gate-staff-pin-submit"
              >
                {pinLoading ? <Loader2 className="w-4 h-4 mx-auto animate-spin" /> : 'Unlock Check-in'}
              </button>
            </form>
          ) : canCheckIn ? (
            <button
              onClick={handleCheckIn}
              disabled={checkingIn}
              className={`w-full py-3 rounded-xl font-bold text-white text-sm tracking-wide transition-all ${
                checkingIn
                  ? 'bg-gray-400 cursor-wait'
                  : 'bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] shadow-lg shadow-emerald-200'
              }`}
              data-testid="checkin-btn"
            >
              {checkingIn ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Checking In...
                </span>
              ) : (
                'CHECK IN'
              )}
            </button>
          ) : data.status !== 'approved' ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center" data-testid="checkin-not-approved">
              <p className="text-amber-700 text-xs font-medium">Check-in available only for approved registrations</p>
            </div>
          ) : null}

          {/* Check-in result feedback */}
          {checkInResult && !checkInResult.success && (
            <p className="text-red-500 text-xs text-center" data-testid="checkin-error">{checkInResult.message}</p>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-3 text-center border-t border-gray-100 flex items-center justify-between">
          <p className="text-[10px] text-gray-400">Brahmkshatriya Marwadi Khatri Association (Mahasabha)</p>
          {gateStaffSession && (
            <button
              onClick={handleGateStaffLogout}
              className="text-[10px] text-gray-500 hover:text-red-600 flex items-center gap-1 underline-offset-2 hover:underline"
              data-testid="gate-staff-logout"
            >
              <LogOut className="w-3 h-3" /> Lock
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailItem({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-gray-400 text-xs">{label}</p>
      <p className="text-gray-800 font-medium">{value || '-'}</p>
    </div>
  );
}
